import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import { Markdown } from "tiptap-markdown";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { Languages, Database, Settings, History, Lock, ChevronDown } from "lucide-react";
import { ProseWrapper, FigmaTypography } from "@libs/zapi-ui/custom";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import {
  VersionHistoryPanel,
  type TVersionHistoryItem
} from "@/zapi-frontend/pages/ProjectsPage/ui/VersionHistoryPanel/VersionHistoryPanel";
import { CaseBriefFlagAffordance } from "@/zapi-frontend/features/FlagDeficiency";
import { CaseBriefEditorToolbar } from "./CaseBriefEditorToolbar";
import { TemplatePlaceholders } from "../lib/templatePlaceholders";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { LanguagePopover, type TLanguageOption } from "./LanguagePopover";
import { VisibilityPopover } from "./VisibilityPopover";
import { DimensionLabel } from "./DimensionLabel";
import { CaseBriefVaultDrawer } from "./CaseBriefVaultDrawer";
import type { TPreUploadedDocument } from "@/zapi-frontend/pages/CreateProjectPage/model/usePreUploadDocuments";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import type { TFunction } from "i18next";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";

export type TCaseBriefVersionHistory = {
  rowProjectId: string;
  versions: TVersionHistoryItem[];
  hasEdit: boolean;
  organizationId: number;
  onInvalidated: () => void;
  onOptimisticRename?: (versionId: string, newName: string) => void;
};

type CaseBriefEditorProps = {
  content: string;
  onUpdate: (content: string) => void;
  projectName?: string;
  projectDescription?: string;
  onProjectNameChange?: (name: string) => void;
  onProjectDescriptionChange?: (description: string) => void;
  language?: string;
  languageActive?: boolean;
  languageOptions?: TLanguageOption[];
  onLanguageChange?: (language: string) => void;
  visibility?: "organization" | "private";
  visibilityActive?: boolean;
  onVisibilityChange?: (v: "organization" | "private") => void;
  showInstructionsChip?: boolean;
  onInstructionsClick?: () => void;
  vaultActive?: boolean;
  vaultDocuments?: TPreUploadedDocument[];
  vaultPendingUploadNames?: string[];
  onVaultAddFiles?: (files: FileList) => void;
  onVaultAddWebLink?: (url: string) => void;
  onVaultRemove?: (documentId: number) => void;
  dimensions?: TOrganizationProjectDimension[];
  dimensionValues?: Record<string, string>;
  onDimensionChange?: (dimensionId: number, value: string) => void;
  versionDisplayName?: string;
  versionHistory?: TCaseBriefVersionHistory;
  readOnly?: boolean;
  caseBriefId?: number | null;
  projectId?: number | null;
};

const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const languageName = (t: TFunction<"projectHome">, value: string) => {
  if (value === "english") return t("projectSetup.languageName.english");
  if (value === "japanese") return t("projectSetup.languageName.japanese");
  return capitalize(value);
};

type TLabelButtonProps = {
  active: boolean;
  disabled?: boolean;
  children: React.ReactNode;
};

const LabelButton = React.forwardRef<
  HTMLButtonElement,
  TLabelButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ active, disabled, children, className, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    disabled={disabled}
    className={cn(
      "border border-innoveraZinc-150 flex gap-1 h-6 items-center px-2 py-[5px] rounded-md transition-colors",
      !disabled && "hover:bg-innoveraZinc-50",
      active ? "text-innoveraZinc-900" : "text-innoveraZinc-400",
      className
    )}
    {...rest}
  >
    {children}
  </button>
));
LabelButton.displayName = "LabelButton";

type TDecisionFrameLabelsProps = {
  language?: string;
  languageActive?: boolean;
  languageOptions?: TLanguageOption[];
  onLanguageChange?: (language: string) => void;
  visibility?: "organization" | "private";
  visibilityActive?: boolean;
  onVisibilityChange?: (v: "organization" | "private") => void;
  showInstructionsChip?: boolean;
  onInstructionsClick?: () => void;
  vaultActive?: boolean;
  vaultDocuments?: TPreUploadedDocument[];
  vaultPendingUploadNames?: string[];
  onVaultAddFiles?: (files: FileList) => void;
  onVaultAddWebLink?: (url: string) => void;
  onVaultRemove?: (documentId: number) => void;
  dimensions?: TOrganizationProjectDimension[];
  dimensionValues?: Record<string, string>;
  onDimensionChange?: (dimensionId: number, value: string) => void;
  versionDisplayName?: string;
  versionHistory?: TCaseBriefVersionHistory;
  readOnly?: boolean;
};

function VersionHistoryChip({
  versionDisplayName,
  versionHistory
}: {
  versionDisplayName: string;
  versionHistory: TCaseBriefVersionHistory;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <LabelButton active>
          <History className="size-3" />
          <FigmaTypography component="span" variant="label-3">
            {displayVersionName(versionDisplayName)}
          </FigmaTypography>
          <ChevronDown
            className={cn("size-3 shrink-0 text-innoveraZinc-400 transition-transform", open && "rotate-180")}
            aria-hidden
          />
        </LabelButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={8}
        className="w-auto overflow-hidden p-0"
        style={{ maxHeight: "var(--radix-popover-content-available-height)" }}
      >
        <VersionHistoryPanel
          rowProjectId={versionHistory.rowProjectId}
          versions={versionHistory.versions}
          hasEdit={versionHistory.hasEdit}
          organizationId={versionHistory.organizationId}
          openInNewTab={false}
          onInvalidated={versionHistory.onInvalidated}
          onOptimisticRename={versionHistory.onOptimisticRename}
        />
      </PopoverContent>
    </Popover>
  );
}

function DecisionFrameLabels({
  language,
  languageActive,
  languageOptions,
  onLanguageChange,
  visibility,
  visibilityActive,
  onVisibilityChange,
  showInstructionsChip,
  onInstructionsClick,
  vaultActive,
  vaultDocuments = [],
  vaultPendingUploadNames,
  onVaultAddFiles,
  onVaultAddWebLink,
  onVaultRemove,
  dimensions = [],
  dimensionValues = {},
  onDimensionChange,
  versionDisplayName,
  versionHistory,
  readOnly = false
}: TDecisionFrameLabelsProps) {
  const { t } = useTranslation("projectHome");
  const isLanguageActive = !!languageActive && !!language;
  const isVaultActive = !!vaultActive;
  const vaultCount = vaultDocuments.length;
  const [isVaultOpen, setIsVaultOpen] = useState(false);

  const canOpenVault = isVaultActive && !!onVaultAddFiles && !!onVaultAddWebLink && !!onVaultRemove && !readOnly;
  const canSwitchLanguage = isLanguageActive && (languageOptions?.length ?? 0) > 1 && !!onLanguageChange && !readOnly;

  const languageButton = (
    <LabelButton active={isLanguageActive} disabled={!canSwitchLanguage}>
      <Languages className="size-3" />
      <FigmaTypography component="span" variant="label-3">
        {isLanguageActive ? languageName(t, language as string) : t("projectSetup.language")}
      </FigmaTypography>
    </LabelButton>
  );

  const firstChip = showInstructionsChip ? (
    <LabelButton active={true} disabled={!onInstructionsClick} onClick={onInstructionsClick}>
      <Settings className="size-3" />
      <FigmaTypography component="span" variant="label-3">
        {t("projectSetup.instructions")}
      </FigmaTypography>
    </LabelButton>
  ) : canSwitchLanguage && languageOptions ? (
    <LanguagePopover options={languageOptions} value={language} onChange={onLanguageChange}>
      {languageButton}
    </LanguagePopover>
  ) : (
    languageButton
  );

  const canSwitchVisibility = !!visibilityActive && !!onVisibilityChange && !readOnly;

  const visibilityButton = (
    <LabelButton active={!!visibilityActive} disabled={!canSwitchVisibility}>
      <Lock className="size-3" />
      <FigmaTypography component="span" variant="label-3">
        {visibility
          ? visibility === "private"
            ? t("projectSetup.private")
            : t("projectSetup.organization")
          : t("projectSetup.visibility")}
      </FigmaTypography>
    </LabelButton>
  );

  const visibilityChip = canSwitchVisibility ? (
    <VisibilityPopover value={visibility ?? "organization"} onChange={onVisibilityChange}>
      {visibilityButton}
    </VisibilityPopover>
  ) : visibility ? (
    visibilityButton
  ) : null;

  return (
    <div className="flex gap-1 items-center flex-wrap mt-2">
      {firstChip}

      {visibilityChip}

      <LabelButton
        active={isVaultActive}
        disabled={!canOpenVault}
        onClick={canOpenVault ? () => setIsVaultOpen(true) : undefined}
      >
        <Database className="size-3" />
        <FigmaTypography component="span" variant="label-3">
          {t("projectSetup.vault")}
        </FigmaTypography>
        <span
          className={cn(
            "flex items-center justify-center px-[5px] py-[3px] rounded text-[12px] leading-[11px] font-medium",
            isVaultActive ? "bg-innoveraZinc-150 text-innoveraZinc-500" : "bg-innoveraZinc-100 text-innoveraZinc-400"
          )}
        >
          {vaultCount}
        </span>
      </LabelButton>

      {versionDisplayName &&
        (versionHistory ? (
          <VersionHistoryChip versionDisplayName={versionDisplayName} versionHistory={versionHistory} />
        ) : (
          <LabelButton active={true} disabled>
            <History className="size-3" />
            <FigmaTypography component="span" variant="label-3">
              {displayVersionName(versionDisplayName)}
            </FigmaTypography>
          </LabelButton>
        ))}

      {dimensions.map((dim) => (
        <DimensionLabel
          key={dim.id}
          dimension={dim}
          value={dimensionValues[String(dim.id)] ?? ""}
          onChange={(id, next) => onDimensionChange?.(id, next)}
          disabled={readOnly || !onDimensionChange}
        />
      ))}

      {canOpenVault && (
        <CaseBriefVaultDrawer
          open={isVaultOpen}
          onOpenChange={setIsVaultOpen}
          documents={vaultDocuments}
          pendingUploadNames={vaultPendingUploadNames}
          onAddFiles={onVaultAddFiles}
          onAddWebLink={onVaultAddWebLink}
          onRemove={onVaultRemove}
        />
      )}
    </div>
  );
}

export function CaseBriefEditor({
  content,
  onUpdate,
  projectName,
  projectDescription,
  onProjectNameChange,
  onProjectDescriptionChange,
  language,
  languageActive,
  languageOptions,
  onLanguageChange,
  visibility,
  visibilityActive,
  onVisibilityChange,
  showInstructionsChip,
  onInstructionsClick,
  vaultActive,
  vaultDocuments,
  vaultPendingUploadNames,
  onVaultAddFiles,
  onVaultAddWebLink,
  onVaultRemove,
  dimensions,
  dimensionValues,
  onDimensionChange,
  versionDisplayName,
  versionHistory,
  readOnly = false,
  caseBriefId,
  projectId
}: CaseBriefEditorProps) {
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [projectDescription]);

  const tEditor = useMemo(() => i18n.getFixedT(projectLanguageToLng(language), "createProject"), [language]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] }
      }),
      Underline,
      Markdown.configure({
        html: true,
        tightLists: false,
        bulletListMarker: "-",
        transformPastedText: false,
        transformCopiedText: false,
        breaks: false
      }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: "underline text-blue-500 hover:text-blue-600 cursor-pointer"
        }
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: "border-collapse w-full"
        }
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: "border-b border-gray-200"
        }
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: "border-b-2 border-gray-300 bg-gray-100 font-semibold text-left p-2 break-words min-w-0"
        }
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: "border border-gray-200 p-2 break-words min-w-0"
        }
      }),
      Placeholder.configure({
        placeholder: tEditor("editor.placeholder")
      }),
      TemplatePlaceholders
    ],
    content,
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      onUpdate(ed.storage.markdown.getMarkdown());
    },
    editorProps: {
      attributes: {
        class: "outline-none min-h-[300px]"
      }
    }
  });

  const showTemplatePrompts = !!useFeatureFlag("case-brief-document-reading");
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.storage.templatePlaceholders.enabled = showTemplatePrompts;
    editor.view.dispatch(editor.state.tr);
  }, [editor, showTemplatePrompts]);

  useEffect(() => {
    if (!editor) return;
    if (editor.storage.markdown.getMarkdown() === content) return;
    editor.commands.setContent(content);
  }, [content, editor]);

  useEffect(() => {
    editor?.setEditable(!readOnly, false);
  }, [editor, readOnly]);

  return (
    <div className="flex w-full h-full mx-auto">
      <div className="w-full flex flex-col bg-card h-full">
        <div className="flex-1 flex flex-col relative min-h-0 pt-10">
          <div className="w-full px-14 max-w-[837px] mx-auto flex flex-col flex-1 min-h-0">
            {(onProjectNameChange || onProjectDescriptionChange) && (
              <div className="flex flex-col gap-1 mb-4">
                <input
                  type="text"
                  value={projectName ?? ""}
                  onChange={(e) => onProjectNameChange?.(e.target.value)}
                  readOnly={readOnly}
                  placeholder={tEditor("editor.projectName")}
                  data-testid="case-brief-editor-project-name-input"
                  className="w-full text-[28px] font-medium leading-[40px] tracking-[-0.28px] text-zinc-900 bg-transparent border-none outline-none placeholder:text-zinc-300"
                />
                <textarea
                  ref={descriptionRef}
                  value={projectDescription ?? ""}
                  onChange={(e) => onProjectDescriptionChange?.(e.target.value)}
                  readOnly={readOnly}
                  placeholder={tEditor("editor.projectDescription")}
                  rows={1}
                  className="w-full text-[14px] leading-[22px] text-zinc-900 bg-transparent border-none outline-none resize-none placeholder:text-zinc-300 overflow-hidden"
                />
                <DecisionFrameLabels
                  language={language}
                  languageActive={languageActive}
                  languageOptions={languageOptions}
                  onLanguageChange={onLanguageChange}
                  visibility={visibility}
                  visibilityActive={visibilityActive}
                  onVisibilityChange={onVisibilityChange}
                  showInstructionsChip={showInstructionsChip}
                  onInstructionsClick={onInstructionsClick}
                  vaultActive={vaultActive}
                  vaultDocuments={vaultDocuments}
                  vaultPendingUploadNames={vaultPendingUploadNames}
                  onVaultAddFiles={onVaultAddFiles}
                  onVaultAddWebLink={onVaultAddWebLink}
                  onVaultRemove={onVaultRemove}
                  dimensions={dimensions}
                  dimensionValues={dimensionValues}
                  onDimensionChange={onDimensionChange}
                  versionDisplayName={versionDisplayName}
                  versionHistory={versionHistory}
                  readOnly={readOnly}
                />
                <div className="border-b border-innoveraZinc-150 mt-3 mb-1" />
              </div>
            )}
            <ProseWrapper editable className="flex-1 overflow-y-auto pb-20">
              <EditorContent
                className="focus-visible:outline-none [&_.ProseMirror]:focus:outline-none text-zinc-700 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none"
                editor={editor}
              />
            </ProseWrapper>
          </div>
          {!readOnly && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
              <CaseBriefEditorToolbar editor={editor} language={language} />
            </div>
          )}
          {caseBriefId ? (
            <CaseBriefFlagAffordance editor={editor} caseBriefId={caseBriefId} projectId={projectId} />
          ) : null}
        </div>
      </div>
    </div>
  );
}
